import { FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { FileUp, Lock, PackagePlus, Plus, Trash2, X } from 'lucide-react';

import {
  factoryOrderOwnerLabel,
  factoryOrderStatusLabel,
  factoryService,
  isFactoryOrderShelfOpen,
  type FactoryOrder,
  type FactoryShelfMaterial,
} from '../../services/factoryService';
import {
  depositCoveredPages,
  depositFenOf,
  fenYuanLabel,
  processingQuotaPages,
  settleAgainstQuote,
  tokenRateYuanOf,
} from '../orderFees';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';

function formatBytes(n: number): string {
  if (!n) return '—';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatShelfWhen(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shelfPageCount(m: FactoryShelfMaterial): number {
  const n = Number(m.pageCount || 0);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function compactCode(value?: string | null): string {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length <= 20) return s;
  return `${s.slice(0, 10)}…${s.slice(-4)}`;
}

function personLabel(name?: string | null, userId?: string | null): string {
  const n = String(name || '').trim();
  if (n) return n;
  const id = String(userId || '').trim();
  if (!id) return '—';
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function orderDepositReady(order: FactoryOrder | null): boolean {
  const status = String(order?.paymentStatus || 'paid');
  return status === 'paid' || status === 'settled';
}

/** Fix UTF-8 filenames that were stored/decoded as latin1 mojibake. */
function fixMojibakeText(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/[\u4e00-\u9fff]/.test(raw)) return raw;
  if (!/[\u0080-\u00FF]/.test(raw)) return raw;
  try {
    const bytes = Uint8Array.from(Array.from(raw, (ch) => ch.charCodeAt(0) & 0xff));
    const fixed = new TextDecoder('utf-8').decode(bytes);
    if (fixed.includes('\uFFFD')) return raw;
    if (/[\u4e00-\u9fff]/.test(fixed)) return fixed;
  } catch {
    /* ignore */
  }
  return raw;
}

function materialTitle(m: FactoryShelfMaterial): string {
  return fixMojibakeText(m.title) || fixMojibakeText(m.filename) || m.title || m.filename || '未命名';
}

/** Orders still open for PDF shelf upload. */
function isShelfBindableOrder(order: FactoryOrder): boolean {
  return isFactoryOrderShelfOpen(order);
}

function isPdfFile(file: File): boolean {
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  if (name.endsWith('.pdf')) return true;
  if (type.includes('pdf')) return true;
  // Some browsers leave type empty / octet-stream for PDFs from disk.
  if (!type || type === 'application/octet-stream' || type === 'binary/octet-stream') {
    return name.length > 0;
  }
  return false;
}

function mergePdfFiles(
  prev: File[],
  incoming: FileList | File[],
  options?: { fromFilePicker?: boolean },
): { files: File[]; rejected: number } {
  const next = [...prev];
  const seen = new Set(prev.map((f) => `${f.name}:${f.size}:${f.lastModified}`));
  let rejected = 0;
  for (const file of Array.from(incoming)) {
    // File picker already constrained by accept=.pdf — trust it. Drag-drop still filters.
    const ok = options?.fromFilePicker ? true : isPdfFile(file);
    if (!ok) {
      rejected += 1;
      continue;
    }
    const key = `${file.name}:${file.size}:${file.lastModified}`;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(file);
  }
  return { files: next, rejected };
}

export function FactoryShelfPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrderId, activeOrder, setActiveOrderId } = useFactoryOrderScope();
  const urlOrderId = String(searchParams.get('orderId') || '').trim();
  const focusOrderId = activeOrderId || urlOrderId;
  const { refreshKey = 0 } = useOutletContext<FactoryDeskOutletContext>() ?? { refreshKey: 0 };
  const [shelf, setShelf] = useState<FactoryShelfMaterial[]>([]);
  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitTarget, setAdmitTarget] = useState<FactoryShelfMaterial | null>(null);
  const [countingIds, setCountingIds] = useState<Set<string>>(() => new Set());
  const countedPagesRef = useRef<Set<string>>(new Set());
  const countingLockRef = useRef(false);
  const shelfMountedRef = useRef(true);
  const [orderId, setOrderId] = useState(focusOrderId);
  const [materialCode, setMaterialCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadLabel, setUploadLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pickerEpoch, setPickerEpoch] = useState(0);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const progressThrottleRef = useRef({ lastPct: -1, lastAt: 0 });

  const bindableOrders = useMemo(() => orders.filter(isShelfBindableOrder), [orders]);
  const focusOrder = activeOrder || orders.find((o) => o.id === focusOrderId) || null;
  const uploadOrders = useMemo(() => {
    if (focusOrder && isShelfBindableOrder(focusOrder)) return [focusOrder];
    return bindableOrders.filter((o) => o.id === focusOrderId);
  }, [bindableOrders, focusOrder, focusOrderId]);
  const visibleShelf = useMemo(() => {
    if (!focusOrderId) return [];
    return shelf.filter((m) => m.orderId === focusOrderId);
  }, [focusOrderId, shelf]);
  const shelfPageTotal = useMemo(
    () => visibleShelf.reduce((sum, m) => sum + shelfPageCount(m), 0),
    [visibleShelf],
  );
  const admittedPageTotal = useMemo(
    () => visibleShelf
      .filter((m) => m.status === 'admitted')
      .reduce((sum, m) => sum + shelfPageCount(m), 0),
    [visibleShelf],
  );
  const shelfOpen = Boolean(focusOrder && isFactoryOrderShelfOpen(focusOrder));
  const coveredPages = depositCoveredPages(focusOrder);
  const pageQuota = processingQuotaPages(focusOrder);
  const depositFen = depositFenOf(focusOrder);
  const pageRemaining = coveredPages > 0 ? coveredPages - admittedPageTotal : 0;
  const supplementDueFen = Math.max(0, Number(focusOrder?.balanceDueFen || 0));

  const reload = useCallback(async () => {
    const [materials, orderList] = await Promise.all([
      factoryService.listShelf(),
      factoryService.listFactoryOrders(),
    ]);
    setShelf(materials);
    setOrders(orderList.filter((o) => !['cancelled', 'closed'].includes(o.status)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        if (refreshKey > 0) setLoading(true);
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, refreshKey]);

  useEffect(() => {
    shelfMountedRef.current = true;
    return () => {
      shelfMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const missing = visibleShelf.filter(
      (m) => m.status !== 'admitted' && shelfPageCount(m) <= 0 && !countedPagesRef.current.has(m.id),
    );
    if (!missing.length || countingLockRef.current) return;
    countingLockRef.current = true;
    void (async () => {
      try {
        for (const material of missing) {
          if (!shelfMountedRef.current) return;
          countedPagesRef.current.add(material.id);
          setCountingIds((prev) => {
            const next = new Set(prev);
            next.add(material.id);
            return next;
          });
          try {
            const updated = await factoryService.countShelfPages(material.id);
            if (!shelfMountedRef.current) return;
            setShelf((prev) => prev.map((row) => (
              row.id === updated.id ? { ...row, pageCount: updated.pageCount } : row
            )));
          } catch {
            /* keep 页数待计 */
          } finally {
            if (shelfMountedRef.current) {
              setCountingIds((prev) => {
                const next = new Set(prev);
                next.delete(material.id);
                return next;
              });
            }
          }
        }
      } finally {
        countingLockRef.current = false;
      }
    })();
  }, [visibleShelf]);

  // Adopt URL order into workspace scope (e.g. old 去上架 deep-link).
  useEffect(() => {
    if (urlOrderId && urlOrderId !== activeOrderId) setActiveOrderId(urlOrderId);
  }, [activeOrderId, setActiveOrderId, urlOrderId]);

  // Deep-link / current order: lock upload to this order.
  useEffect(() => {
    if (!focusOrderId) return;
    setOrderId(focusOrderId);
    if (focusOrder && isFactoryOrderShelfOpen(focusOrder) && urlOrderId) {
      setUploadOpen(true);
    }
  }, [focusOrder, focusOrderId, urlOrderId]);

  function abortUpload() {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
  }

  async function onShelfUpload(selectedFiles: File[]) {
    if (!selectedFiles.length) {
      setError('请先选择至少一个 PDF 文件');
      return;
    }
    if (!orderId.trim() && !materialCode.trim()) {
      setError('请选择工厂订单（或填写素材码）');
      return;
    }

    abortUpload();
    const ac = new AbortController();
    uploadAbortRef.current = ac;
    progressThrottleRef.current = { lastPct: -1, lastAt: 0 };

    setBusy(true);
    setError(null);
    setUploadPct(0);
    const total = selectedFiles.length;
    const uploadOrderId = orderId.trim() || undefined;
    const uploadMaterialCode = materialCode.trim() || undefined;

    try {
      for (let i = 0; i < total; i += 1) {
        if (ac.signal.aborted) throw new Error('已取消上传');
        const file = selectedFiles[i]!;
        setUploadLabel(`上传 ${i + 1}/${total}：${file.name}`);
        await factoryService.uploadToShelf(file, {
          orderId: uploadOrderId,
          materialCode: uploadMaterialCode,
          signal: ac.signal,
          onProgress: (pct) => {
            const overall = Math.min(100, Math.round(((i + pct / 100) / total) * 100));
            const now = Date.now();
            const throttle = progressThrottleRef.current;
            if (overall === throttle.lastPct) return;
            if (overall < 100 && now - throttle.lastAt < 160) return;
            throttle.lastPct = overall;
            throttle.lastAt = now;
            setUploadPct(overall);
          },
        });
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      setUploadOpen(false);
      setMaterialCode('');
      setUploadLabel('');
      setPickerEpoch((n) => n + 1);
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (!/已取消上传/.test(message)) setError(message || '上架失败');
    } finally {
      if (uploadAbortRef.current === ac) uploadAbortRef.current = null;
      setBusy(false);
      setUploadPct(null);
      setUploadLabel('');
    }
  }

  async function onAdmit(e: FormEvent) {
    e.preventDefault();
    if (!admitTarget) return;
    setBusy(true);
    setError(null);
    try {
      const itemPages = shelfPageCount(admitTarget);
      if (focusOrder && itemPages > 0 && pageQuota > 0 && admittedPageTotal + itemPages > pageQuota) {
        await factoryService.requestPageSupplement(
          focusOrder.id,
          admittedPageTotal + itemPages,
        );
      }
      const { batch } = await factoryService.admitShelfMaterial(admitTarget.id);
      setAdmitOpen(false);
      setAdmitTarget(null);
      await reload();
      navigate(`/factory/tasks/${batch.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(material: FactoryShelfMaterial) {
    if (!confirm(`从货架移除「${material.filename}」？`)) return;
    setBusy(true);
    setError(null);
    try {
      await factoryService.deleteShelfMaterial(material.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function openUpload(forOrderId?: string) {
    const nextId = String(forOrderId || focusOrderId || '').trim();
    abortUpload();
    setError(null);
    setMaterialCode('');
    setUploadPct(null);
    setUploadLabel('');
    setBusy(false);
    setOrderId(nextId);
    setPickerEpoch((n) => n + 1);
    setUploadOpen(true);
    if (nextId) setActiveOrderId(nextId);
  }

  function closeUploadModal() {
    abortUpload();
    setBusy(false);
    setUploadPct(null);
    setUploadLabel('');
    setUploadOpen(false);
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">源货架</h1>
          <p className="mt-1 text-sm text-muted">
            当前订单的原件入库。文件名与页数是工作量；进厂即开工。
          </p>
        </div>
        {shelfOpen ? (
          <button
            type="button"
            onClick={() => openUpload()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm text-white"
          >
            <Plus size={16} />
            上架
          </button>
        ) : null}
      </header>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {focusOrderId ? (
        <div className="rounded-md border border-[#2EC4B6]/40 bg-[#2EC4B6]/10 px-3 py-2 text-sm text-[#2EC4B6]">
          <p>
            当前订单货架
            {focusOrder ? ` · 订单主 ${factoryOrderOwnerLabel(focusOrder)}` : ''}
            {focusOrder && !shelfOpen ? '（已验收，仅可查看已上架素材）' : ''}
          </p>
          {focusOrder ? (
            <p className="mt-1 text-xs tabular-nums text-[#2EC4B6]/90">
              定金 {fenYuanLabel(depositFen)}
              {coveredPages > 0 ? ` · 可进厂 ${coveredPages} 页` : ''}
              {` · 已进厂 ${admittedPageTotal} 页`}
              {coveredPages > 0
                ? (pageRemaining >= 0
                  ? ` · 剩余 ${pageRemaining} 页`
                  : ` · 超出 ${-pageRemaining} 页`)
                : ''}
              {supplementDueFen > 0 ? ` · 已通知补页 ${fenYuanLabel(supplementDueFen)}` : ''}
              {admittedPageTotal > 0 ? ' · 已进厂定金不退' : ''}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="rounded-md border border-[#3a4450] bg-[#1e242c] px-3 py-2 text-sm text-[#9AA3B0]">
          请先在「订单」选择当前订单，再查看该单货架。
        </div>
      )}

      <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        {loading ? <p className="text-sm text-muted">加载中…</p> : null}
        {!loading && visibleShelf.length === 0 ? (
          <p className="text-sm text-muted">
            {focusOrderId
              ? (shelfOpen ? '该订单下暂无货架素材。可点「上架」上传 PDF。' : '该订单下暂无货架素材。')
              : '请先在「订单」选择当前订单。'}
          </p>
        ) : null}
        {!loading && visibleShelf.length > 0 ? (
          <p className="mb-3 text-xs text-muted">
            {visibleShelf.length} 份
            {shelfPageTotal > 0 ? ` · 共 ${shelfPageTotal} 页` : ''}
          </p>
        ) : null}
        <ul className="divide-y divide-border">
          {visibleShelf.map((m) => {
            const locked = m.status === 'admitted';
            const pages = shelfPageCount(m);
            const when = formatShelfWhen(m.createdAt);
            const counting = countingIds.has(m.id);
            const paid = orderDepositReady(focusOrder);
            const metaBits = [
              focusOrder?.orderCode ? `订单 ${focusOrder.orderCode}` : null,
              m.materialCode ? `素材 ${compactCode(m.materialCode)}` : null,
              `订单主 ${personLabel(m.ownedByDisplayName, m.ownedByUserId)}`,
              `上传 ${personLabel(m.uploadedByDisplayName, m.uploadedByUserId)}`,
            ].filter(Boolean);
            return (
              <li
                key={m.id}
                className={`flex flex-wrap items-start justify-between gap-x-4 gap-y-3 py-3.5 first:pt-0 last:pb-0 ${
                  locked ? 'opacity-90' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    {locked ? (
                      <Lock size={14} className="shrink-0 text-primary" aria-hidden />
                    ) : null}
                    <div className="truncate text-[15px] font-semibold tracking-tight text-ink" title={materialTitle(m)}>
                      {materialTitle(m)}
                    </div>
                    {locked ? (
                      <span className="shrink-0 rounded-md border border-primary/35 bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                        已进厂
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted">
                        待进厂
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {formatBytes(m.byteSize)}
                    {when ? ` · ${when}` : ''}
                  </p>
                  {metaBits.length ? (
                    <p className="mt-1 text-[11px] leading-relaxed text-muted" title={metaBits.join(' · ')}>
                      {metaBits.join(' · ')}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  <div className="min-w-[3.5rem] text-right">
                    {pages > 0 ? (
                      <>
                        <p className="tabular-nums text-xl font-semibold leading-none text-ink">{pages}</p>
                        <p className="mt-0.5 text-[11px] text-muted">页</p>
                      </>
                    ) : counting ? (
                      <p className="text-xs text-muted">计页中…</p>
                    ) : (
                      <p className="text-xs text-muted">页数待计</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!locked ? (
                      <>
                        <button
                          type="button"
                          disabled={busy || !paid}
                          title={!paid ? '定金未付，不能进厂' : undefined}
                          onClick={() => {
                            setAdmitTarget(m);
                            setAdmitOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary disabled:opacity-50"
                        >
                          <PackagePlus size={14} />
                          进厂
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onRemove(m)}
                          className="rounded-lg px-2 py-1.5 text-muted hover:text-danger disabled:opacity-50"
                          aria-label="移除"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={!m.batchId}
                        onClick={() => m.batchId && navigate(`/factory/tasks/${m.batchId}`)}
                        className="inline-flex items-center gap-1 rounded-lg border border-primary/40 px-3 py-1.5 text-sm text-primary disabled:opacity-40"
                      >
                        <Lock size={14} />
                        查看任务
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <ShelfUploadModal
        key={pickerEpoch}
        open={uploadOpen}
        busy={busy && uploadOpen}
        uploadPct={uploadPct}
        uploadLabel={uploadLabel}
        orderId={orderId}
        orderLocked={Boolean(focusOrderId)}
        orders={uploadOrders}
        materialCode={materialCode}
        error={error}
        onOrderIdChange={setOrderId}
        onMaterialCodeChange={setMaterialCode}
        onError={setError}
        onClose={closeUploadModal}
        onSubmit={(selected) => void onShelfUpload(selected)}
      />

      <AdmitModal
        open={admitOpen}
        busy={busy && admitOpen}
        material={admitTarget}
        order={focusOrder}
        paid={orderDepositReady(focusOrder)}
        coveredPages={coveredPages}
        consumedPages={admittedPageTotal}
        pageQuota={pageQuota}
        onClose={() => {
          if (!busy) {
            setAdmitOpen(false);
            setAdmitTarget(null);
          }
        }}
        onSubmit={onAdmit}
      />
    </div>
  );
}

function ShelfUploadModal(props: {
  open: boolean;
  busy: boolean;
  uploadPct: number | null;
  uploadLabel: string;
  orderId: string;
  orderLocked?: boolean;
  orders: FactoryOrder[];
  materialCode: string;
  error: string | null;
  onOrderIdChange: (v: string) => void;
  onMaterialCodeChange: (v: string) => void;
  onError: (message: string | null) => void;
  onClose: () => void;
  onSubmit: (files: File[]) => void;
}) {
  const {
    open,
    busy,
    uploadPct,
    uploadLabel,
    orderId,
    orderLocked,
    orders,
    materialCode,
    error,
    onOrderIdChange,
    onMaterialCodeChange,
    onError,
    onClose,
    onSubmit,
  } = props;
  const titleId = useId();
  const inputId = useId();
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const ignoreBackdropUntilRef = useRef(0);
  const selectedOrder = orders.find((o) => o.id === orderId) || null;
  const canSubmit = files.length > 0 && Boolean(orderId.trim() || materialCode.trim());
  const uploading = busy || uploadPct != null;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  function armBackdropGuard() {
    // Native file dialog can deliver a ghost click to the overlay after confirm.
    ignoreBackdropUntilRef.current = Date.now() + 1200;
  }

  function addIncoming(incoming: FileList | File[] | null | undefined, fromFilePicker: boolean) {
    if (!incoming?.length) return;
    const list = Array.from(incoming);
    setFiles((prev) => {
      const { files: next, rejected } = mergePdfFiles(prev, list, { fromFilePicker });
      if (rejected > 0 && next.length === prev.length) {
        onError('未识别到 PDF，请选择 .pdf 文件');
      } else if (next.length > prev.length) {
        onError(null);
      }
      return next;
    });
  }

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 px-4 pb-8 pt-[min(14vh,5.5rem)]"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget || busy) return;
        if (Date.now() < ignoreBackdropUntilRef.current) return;
        onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-md border border-[#3a4450] bg-[#1e242c] p-5 text-[#E8ECF1] shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-semibold text-[#F2F5F8]">
              上架源素材
            </h2>
            <p className="mt-1 text-sm text-[#9AA3B0]">
              绑定已接订单，可一次选择多个 PDF（订单与货架一对多）。
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="rounded-md p-1.5 text-[#8B94A3] hover:bg-white/5 hover:text-[#2EC4B6]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {error ? (
            <p className="rounded-md border border-[#F07178]/40 bg-[#F07178]/10 px-3 py-2 text-sm text-[#F07178]">
              {error}
            </p>
          ) : null}
          <label className="block">
            <span className="text-sm text-[#C5CDD8]">工厂订单</span>
            <select
              value={orderId}
              disabled={busy || Boolean(orderLocked)}
              onChange={(e) => onOrderIdChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-sm text-[#E8ECF1] outline-none focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
            >
              <option value="">选择已接订单…</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.orderCode} · 订单主 {factoryOrderOwnerLabel(o)}（{factoryOrderStatusLabel[o.status] || o.status}）
                </option>
              ))}
            </select>
            {selectedOrder?.orderCode ? (
              <p className="mt-2 rounded-md border border-[#2EC4B6]/35 bg-[#2EC4B6]/10 px-3 py-2 font-mono text-sm text-[#2EC4B6]">
                订单码 {selectedOrder.orderCode}
                {files.length ? ` · 已选 ${files.length} 个文件` : ''}
              </p>
            ) : orderId ? (
              <p className="mt-2 text-xs text-[#E8B86D]">
                订单列表加载中或订单不在可上架状态。若已接单，请稍后重试或刷新。
              </p>
            ) : null}
          </label>
          <label className="block">
            <span className="text-sm text-[#C5CDD8]">素材码（可选）</span>
            <input
              type="text"
              value={materialCode}
              disabled={busy}
              onChange={(e) => onMaterialCodeChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-sm text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
              placeholder="有订单时默认用文件 SHA-256；无订单填已绑定码"
            />
          </label>
          <div>
            <span className="text-sm text-[#C5CDD8]">文档（可多选）</span>
            <label
              htmlFor={busy ? undefined : inputId}
              onClick={armBackdropGuard}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                armBackdropGuard();
                addIncoming(e.dataTransfer.files, false);
              }}
              className={[
                'relative mt-1 flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-8 text-center transition',
                busy ? 'pointer-events-none opacity-50' : '',
                dragOver
                  ? 'border-[#2EC4B6] bg-[#2EC4B6]/10'
                  : 'border-[#3a4450] bg-[#15191f] hover:border-[#2EC4B6]/50',
              ].join(' ')}
            >
              <input
                id={inputId}
                type="file"
                accept="application/pdf,.pdf,application/x-pdf"
                multiple
                disabled={busy}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                onClick={armBackdropGuard}
                onChange={(e) => {
                  armBackdropGuard();
                  const picked = e.target.files;
                  if (picked?.length) addIncoming(picked, true);
                  // Defer clear so some browsers keep FileList until handler finishes.
                  window.setTimeout(() => {
                    e.target.value = '';
                  }, 0);
                }}
              />
              <FileUp size={28} className="pointer-events-none text-[#2EC4B6]" />
              <span className="pointer-events-none text-sm text-[#9AA3B0]">
                {files.length ? `已选 ${files.length} 个，可继续添加` : '拖拽或点击选择多个 PDF'}
              </span>
            </label>
            {files.length ? (
              <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-md border border-[#3a4450] bg-[#15191f] p-2">
                {files.map((f, index) => (
                  <li
                    key={`${f.name}:${f.size}:${f.lastModified}:${index}`}
                    className="flex items-center gap-2 text-xs text-[#C5CDD8]"
                  >
                    <span className="min-w-0 flex-1 truncate" title={f.name}>
                      {f.name}
                    </span>
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => {
                        if (uploading) return;
                        setFiles((prev) => prev.filter((_, i) => i !== index));
                      }}
                      className="shrink-0 rounded px-1.5 py-0.5 text-[#8B94A3] hover:bg-white/5 hover:text-[#F07178] disabled:pointer-events-none disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#8B94A3]"
                      aria-label={`移除 ${f.name}`}
                    >
                      移除
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {uploadPct != null ? (
            <div className="space-y-1">
              {uploadLabel ? <p className="truncate text-xs text-[#9AA3B0]">{uploadLabel}</p> : null}
              <div className="h-1.5 overflow-hidden rounded-full bg-[#15191f]">
                <div className="h-full bg-[#2EC4B6] transition-[width] duration-150" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm text-[#8B94A3] hover:text-[#E8ECF1]"
            >
              {busy ? '取消上传' : '取消'}
            </button>
            <button
              type="button"
              disabled={busy || !canSubmit}
              onClick={() => onSubmit(files)}
              className="rounded-md bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
            >
              {busy
                ? (uploadLabel || '上架中…')
                : files.length > 1
                  ? `上架 ${files.length} 个 PDF`
                  : '上架'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AdmitModal(props: {
  open: boolean;
  busy: boolean;
  material: FactoryShelfMaterial | null;
  order: FactoryOrder | null;
  paid: boolean;
  coveredPages: number;
  consumedPages: number;
  pageQuota: number;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const { open, busy, material, order, paid, coveredPages, consumedPages, pageQuota, onClose, onSubmit } = props;
  const titleId = useId();
  if (!open || !material || typeof document === 'undefined') return null;
  const pages = shelfPageCount(material);
  const owner = personLabel(material.ownedByDisplayName, material.ownedByUserId);
  const nextPages = consumedPages + pages;
  const overflow = paid && pages > 0 && pageQuota > 0 && nextPages > pageQuota;
  const extraPages = overflow ? Math.max(0, nextPages - coveredPages) : 0;
  const depositFen = depositFenOf(order);
  const rate = tokenRateYuanOf(order);
  const supplementFen = overflow
    ? settleAgainstQuote({
      depositFen,
      actualPages: nextPages,
      tokenRateYuan: rate,
    }).supplementFen
    : 0;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 px-4 pb-8 pt-[min(14vh,5.5rem)]"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-md border border-[#3a4450] bg-[#1e242c] p-5 text-[#E8ECF1] shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
      >
        <h2 id={titleId} className="text-lg font-semibold text-[#F2F5F8]">
          {overflow ? '超出定金页量' : '进厂开始加工'}
        </h2>
        <p className="mt-1 text-sm text-[#9AA3B0]">{materialTitle(material)}</p>
        <dl className="mt-3 space-y-1.5 text-sm text-[#C5CDD8]">
          <div className="flex justify-between gap-3">
            <dt className="text-[#8B94A3]">本份工作量</dt>
            <dd className="tabular-nums font-medium text-[#F2F5F8]">
              {pages > 0 ? `${pages} 页` : '页数待计（进厂时回填）'}
            </dd>
          </div>
          {coveredPages > 0 ? (
            <div className="flex justify-between gap-3">
              <dt className="text-[#8B94A3]">定金可进厂</dt>
              <dd className="tabular-nums">
                {fenYuanLabel(depositFen)} · {coveredPages} 页
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-3">
            <dt className="text-[#8B94A3]">已进厂</dt>
            <dd className="tabular-nums">{consumedPages} 页</dd>
          </div>
          {overflow ? (
            <div className="flex justify-between gap-3">
              <dt className="text-[#8B94A3]">进厂后共计</dt>
              <dd className="tabular-nums font-medium text-[#F2F5F8]">
                {nextPages} 页 · 超出 {extraPages} 页
              </dd>
            </div>
          ) : null}
          {order?.orderCode ? (
            <div className="flex justify-between gap-3">
              <dt className="text-[#8B94A3]">订单</dt>
              <dd className="font-mono text-[#E8ECF1]">{order.orderCode}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-3">
            <dt className="text-[#8B94A3]">订单主</dt>
            <dd>{owner}</dd>
          </div>
        </dl>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          {overflow ? (
            <>
              <p className="text-sm text-[#C5CDD8]">
                将向订单主发起补充页量订单
                {supplementFen > 0 ? ` ${fenYuanLabel(supplementFen)}` : ''}
                ，按进厂后共 {nextPages} 页计。
              </p>
              <p className="text-xs leading-relaxed text-[#8B94A3]">
                发送后立即进厂开工。已进厂部分定金不可退。不发送则本份不进厂，避免垫付 Token。
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#C5CDD8]">
                定金已付则立即开工：版面拆分 → 结构聚合 → 颗粒提取 → 插图提取 → 品题推荐。
              </p>
              <p className="text-xs leading-relaxed text-[#8B94A3]">
                进厂后订单不可取消、定金不退。Token 从此刻起消耗。
              </p>
            </>
          )}
          {!paid ? (
            <p className="rounded-md border border-[#F07178]/40 bg-[#F07178]/10 px-3 py-2 text-sm text-[#F07178]">
              定金尚未支付，不能进厂。
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm text-[#8B94A3] hover:text-[#E8ECF1]"
            >
              {overflow ? '暂不发送' : '取消'}
            </button>
            <button
              type="submit"
              disabled={busy || !paid}
              className="rounded-md bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
            >
              {busy
                ? (overflow ? '发送并进厂…' : '进厂中…')
                : overflow
                  ? '发送并进厂'
                  : '确认进厂'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
